import { captureException } from "@sentry/react";
import { reportTelemetryUrl } from "@/lib/reportTelemetry";
import { config } from "@/lib/config";
import { authService } from "@/services/AuthService";

export class ReportRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ReportRequestError";
  }
}

// Market links are public; the report page handles a legal report's sign-in requirement.
export async function reportRequest<T>(
  path: string,
  signal?: AbortSignal,
  blob = false,
): Promise<T> {
  const user = await authService.getUser().catch(() => null);
  const token = user && !user.expired ? user.access_token : null;
  let response: Response;
  try {
    response = await fetch(`${config.VITE_API_URL}/api${path}`, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
      signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch (error) {
    if (!signal?.aborted)
      captureException(new Error("Report service unreachable"), {
        tags: { report_operation: reportTelemetryUrl(path) },
      });
    throw error;
  }
  if (!response.ok) {
    if (response.status >= 500)
      captureException(new Error("Report service failed"), {
        tags: {
          report_operation: reportTelemetryUrl(path),
          http_status: response.status,
        },
      });
    const value = await response.json().catch(() => ({}));
    throw new ReportRequestError(
      value.error ?? "This report is unavailable. Please try again.",
      response.status,
    );
  }
  return (blob ? await response.blob() : await response.json()) as T;
}
